import { useInView } from '../hooks/useInView';

const metrics = [
  { label:'Confidence', sub:'Problem-solving skills', before:3.04, after:4.35, beforePct:60.7, afterPct:87.0, improved:80,
    meaning:'Students rated their ability to identify, analyse, and resolve real-world problems.' },
  { label:'Sensitivity', sub:'To citizen issues', before:3.52, after:4.15, beforePct:70.4, afterPct:83.0, improved:56,
    meaning:'How aware and attuned students felt to everyday justice challenges faced by ordinary citizens.' },
  { label:'Pedagogy', sub:'Hands-on vs lectures', before:4.00, after:4.72, beforePct:80.0, afterPct:94.4, improved:54,
    meaning:'Agreement that activity-based methods are more effective than traditional lectures. Started high and ended near-unanimous.' },
  { label:'Caste', sub:'Identity understanding', before:3.39, after:4.06, beforePct:67.8, afterPct:81.1, improved:54,
    meaning:'Basic to Good understanding. Students developed sharper awareness of how caste shapes access to justice.' },
  { label:'Gender', sub:'Identity understanding', before:3.63, after:4.13, beforePct:72.6, afterPct:82.6, improved:44,
    meaning:'Students strengthened their understanding of gender as a lived justice issue beyond textbook definitions.' },
  { label:'Religion', sub:'Identity understanding', before:3.43, after:3.93, beforePct:68.5, afterPct:78.5, improved:41,
    meaning:'Basic to Good understanding. Students engaged with religious identity as a factor in justice access.' },
];

const SIZE = 150;
const STROKE = 11;
const R = (SIZE - STROKE * 2) / 2;
const CIRC = 2 * Math.PI * R;

function Ring({ metric, active, delay }) {
  const beforeDash = CIRC * (metric.beforePct / 100);
  const afterDash  = CIRC * (metric.afterPct  / 100);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
      <div style={{ display:'flex', justifyContent:'center' }}>
        <div style={{ position:'relative', width:SIZE, height:SIZE }}>
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ transform:'rotate(-90deg)' }}>
            {/* Track */}
            <circle cx={SIZE/2} cy={SIZE/2} r={R} fill="none" stroke="#EBEBEB" strokeWidth={STROKE} />

            {/* BEFORE arc — solid gray, full thickness */}
            <circle cx={SIZE/2} cy={SIZE/2} r={R} fill="none"
              stroke="#A0A09A" strokeWidth={STROKE}
              strokeDasharray={`${active ? beforeDash : 0} ${CIRC}`}
              strokeLinecap="round"
              style={{ transition:`stroke-dasharray 1s cubic-bezier(0.25,1,0.5,1) ${delay}s` }}
            />

            {/* AFTER arc — teal at 55% opacity so gray before arc shows through */}
            <circle cx={SIZE/2} cy={SIZE/2} r={R} fill="none"
              stroke="rgba(10,191,188,0.58)" strokeWidth={STROKE}
              strokeDasharray={`${active ? afterDash : 0} ${CIRC}`}
              strokeLinecap="round"
              style={{ transition:`stroke-dasharray 1.2s cubic-bezier(0.25,1,0.5,1) ${delay + 0.2}s` }}
            />
          </svg>

          {/* Center label */}
          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
            <div style={{ fontFamily:'var(--font-head)', fontSize:'1.55rem', fontWeight:700, color:'var(--accent)', lineHeight:1 }}>{metric.after.toFixed(1)}</div>
            <div style={{ fontFamily:'var(--font-head)', fontSize:'0.55rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)' }}>after</div>
          </div>
        </div>
      </div>

      {/* Info card below ring */}
      <div className="nb-card" style={{ padding:'1.25rem', flex:1 }}>
        <div style={{ fontFamily:'var(--font-head)', fontSize:'1.05rem', fontWeight:700, marginBottom:'2px' }}>{metric.label}</div>
        <div style={{ fontFamily:'var(--font-head)', fontSize:'0.6rem', fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.75rem' }}>{metric.sub}</div>

        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'0.6rem', flexWrap:'wrap' }}>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'1.05rem', fontWeight:700, color:'#A0A09A' }}>{metric.before.toFixed(1)}</span>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'0.7rem', color:'#CCC' }}>→</span>
          <span style={{ fontFamily:'var(--font-head)', fontSize:'1.05rem', fontWeight:700, color:'var(--accent)' }}>{metric.after.toFixed(1)}</span>
          <span className="tag tag-accent" style={{ fontSize:'0.55rem', padding:'2px 8px' }}>{metric.improved}% up</span>
        </div>

        <p style={{ fontFamily:'var(--font-body)', fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6 }}>{metric.meaning}</p>
      </div>
    </div>
  );
}

export default function TransformationRings() {
  const [ref, inView] = useInView({ threshold: 0.08 });
  return (
    <section id="transformation" className="section-alt">
      <div className="container" ref={ref}>

        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'1.5rem' }}>
          <p className="eyebrow">Before → After</p>
          <h2 className="h2">What changed</h2>
          <p className="body-lg" style={{ maxWidth:'560px', marginTop:'0.75rem' }}>
            Every ring shows two arcs. Gray is where students started. Teal is where they ended. The gap between them is the workshop's impact.
          </p>
        </div>

        {/* Legend */}
        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'3rem', transitionDelay:'0.08s', display:'flex', gap:'2rem', flexWrap:'wrap', alignItems:'center' }}>
          {[['#A0A09A','Before workshop'],['rgba(10,191,188,0.58)','After workshop']].map(([col, lbl]) => (
            <div key={lbl} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <div style={{ width:28, height:4, background:col, borderRadius:2 }} />
              <span style={{ fontFamily:'var(--font-head)', fontSize:'0.72rem', fontWeight:600, color:'var(--muted)' }}>{lbl}</span>
            </div>
          ))}
          <span style={{ fontFamily:'var(--font-head)', fontSize:'0.65rem', color:'var(--muted)', borderLeft:'2px solid #DDD', paddingLeft:'1.5rem' }}>
            Scale: 1 (very little) to 5 (nuanced)
          </span>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'2rem' }}>
          {metrics.map((m, i) => (
            <div key={i} className={`reveal ${inView?'visible':''}`} style={{ transitionDelay:`${i*0.08}s` }}>
              <Ring metric={m} active={inView} delay={i*0.12} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
